import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { canCarryPregnancy } from '../../pedigree-tool/pedigree/config';
import {
  PregnancyModal,
  DeleteConfirm,
  usePregnancyCrud,
  OUTCOME_LABELS,
  spouseOptionsFor
} from '../../pedigree-tool/canvas-tools/options/pregnancyEditor';
import {
  recordCardTint,
  recordCardTintBorder
} from './clinical-notes-section/recordCardStyles';

// "Reproductive" accordion on a carrier's per-person sidebar (Track B). Lists the carrier's
// pregnancy RECORDS (Pregnancy/PregnancyChanged) as tiles styled to match the Clinical
// Records tiles (coloured left bar + low-opacity tint + type as the title), for consistency
// across the app. The tile is a plain clickable card — click opens the shared editor modal
// (add/edit + hide + delete all live in the modal). The carrier here is a real person; the
// pregnancies/donors it produces are DERIVED render nodes, never members. Gated into the
// sidebar on config.ENABLE_REPRO_NODES (see util.getUserSidebarItems).

// Pregnancy accent — a darker pink; the tile background is this at low opacity (via the same
// recordCardTint helper the Clinical Records tiles use, so the surfaces can't drift).
const PREG_ACCENT = '#c2185b';

const CONCEIVED_LABEL = {
  partner: 'Partner',
  spermDonor: 'Sperm donor',
  eggDonor: 'Egg donor',
  embryoDonor: 'Donated embryo',
  partnerAndDonor: 'Partner + donor'
};

const outcomeLabel = (outcome) =>
  (OUTCOME_LABELS.find(([value]) => value === outcome) || [null, outcome])[1];

function PregnancyTile({ record, onOpen }) {
  const hidden = !!record.hidden;
  const weeks = record.gestationalWeeks
    ? ` · ${record.gestationalWeeks}w`
    : '';
  return (
    <Card
      sx={{
        border: `1px solid ${recordCardTintBorder(PREG_ACCENT)}`,
        borderLeft: `3px solid ${PREG_ACCENT}`,
        backgroundColor: recordCardTint(PREG_ACCENT),
        boxShadow: 'none',
        borderRadius: '6px',
        opacity: hidden ? 0.55 : 1,
        transition: 'transform .15s ease, box-shadow .15s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(31, 42, 51, .13)'
        }
      }}
    >
      <CardActionArea onClick={onOpen}>
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: PREG_ACCENT,
              lineHeight: 1
            }}
          >
            Pregnancy{hidden ? ' · hidden' : ''}
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
            <div>
              {outcomeLabel(record.outcome)}
              {weeks}
            </div>
            <div>
              Conceived with {CONCEIVED_LABEL[record.conceivedWith] || '—'}
            </div>
            {record.karyotype ? <div>{record.karyotype}</div> : null}
            {record.comments ? (
              <div style={{ fontStyle: 'italic' }}>{record.comments}</div>
            ) : null}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function ReproductiveSection({
  data,
  user,
  memberRecords,
  setMemberRecords,
  setPedigreeState,
  modificationHistoryHandle,
  isDisabled
}) {
  const { setSnack } = React.useContext(SnackContext);
  // A modal target: { record } for edit, { record: null } for add; null = closed.
  const [modalTarget, setModalTarget] = React.useState(null);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const { busy, submitPregnancy, deletePregnancy, convertToLiveBirth } =
    usePregnancyCrud({
      memberRecords,
      setMemberRecords,
      setPedigreeState,
      setSnack,
      user
    });

  // The carrier is the selected member as held in memberRecords (source of truth for _id +
  // pregnancies); fall back to `data` so the panel still renders before records hydrate.
  const carrier =
    (memberRecords || []).find((m) => m.pid === data?.pid) || data;
  const pregnancies = carrier?.pregnancies || [];
  const canCarry = canCarryPregnancy(data);
  const spouseOptions = spouseOptionsFor(carrier, memberRecords);

  // Return the result; the modal decides whether to close or prompt to add the live-birth
  // child (it calls onClose itself).
  const handleSubmit = (formValues) =>
    submitPregnancy(carrier, modalTarget?.record, formValues);

  const handleDelete = async () => {
    const ok = await deletePregnancy(carrier, deleteTarget);
    if (ok) setDeleteTarget(null);
  };

  return (
    <Box>
      <Stack spacing={1}>
        {pregnancies.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No pregnancies recorded.
          </Typography>
        ) : (
          pregnancies.map((record) => (
            <PregnancyTile
              key={String(record._id)}
              record={record}
              onOpen={() => setModalTarget({ record })}
            />
          ))
        )}

        <Tooltip
          title={
            canCarry
              ? ''
              : 'Set Sex Assigned at Birth to Female before adding a pregnancy'
          }
          placement="top"
        >
          <span>
            <Button
              startIcon={<AddIcon />}
              size="small"
              variant="outlined"
              disabled={!canCarry || busy || isDisabled}
              onClick={() => setModalTarget({ record: null })}
              fullWidth
            >
              Add pregnancy
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {modalTarget && (
        <PregnancyModal
          record={modalTarget.record}
          spouseOptions={spouseOptions}
          onSubmit={handleSubmit}
          onClose={() => setModalTarget(null)}
          onConvert={(rec, gender) => convertToLiveBirth(carrier, rec, gender)}
          onDelete={
            modalTarget.record
              ? () => {
                  const rec = modalTarget.record;
                  setModalTarget(null);
                  setDeleteTarget(rec);
                }
              : undefined
          }
          historyAction={
            modalTarget.record && modificationHistoryHandle
              ? () =>
                  modificationHistoryHandle(
                    CONSTANTS.auditLogType.reproductive,
                    {},
                    String(modalTarget.record._id)
                  )
              : undefined
          }
        />
      )}
      {deleteTarget && (
        <DeleteConfirm
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
          deleting={busy}
        />
      )}
    </Box>
  );
}
